import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { ArrowLeft, RotateCw, ShieldOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Stat, StatusBadge } from "@/components/Common";
import { KeyRevealDialog } from "@/components/KeyDialog";
import { ReceivedChart } from "@/components/ReceivedChart";
import { api, errorText, fetcher } from "@/lib/api";
import { fmtBytes, fmtDuration, fmtNum, fmtTime, MODE_LABELS, relTime } from "@/lib/format";

function Confirm({ open, onOpenChange, title, text, action, onConfirm, testId }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl" data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{text}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid={`${testId}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`${testId}-confirm`}>{action}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ExportTable({ tables }) {
  return (
    <div className="surface overflow-hidden">
      <div className="flex items-center justify-between px-5 pt-5">
        <h2 className="text-base font-semibold md:text-lg">Tables</h2>
        <span className="text-xs text-muted-foreground">{tables.filter((t) => !t.excluded).length} tables{tables.some((t) => t.excluded) ? ` · ${tables.filter((t) => t.excluded).length} excluded on the server` : ""}</span>
      </div>
      <div className="mt-3 overflow-x-auto">
        <Table data-testid="device-tables-table">
          <TableHeader>
            <TableRow>
              <TableHead>Table</TableHead><TableHead>Sync method</TableHead><TableHead className="w-56">Initial export</TableHead>
              <TableHead className="text-right">Source rows</TableHead><TableHead className="text-right">Size</TableHead><TableHead>Last received</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tables.filter((t) => !t.excluded).map((t) => {
              const done = t.export_status === "done";
              const total = t.export_total || 0;
              const read = Math.min(total || Infinity, t.export_read ?? t.export_done ?? 0);
              const confirmed = Math.min(total || Infinity, t.export_done || 0);
              const pct = done ? 100 : total ? Math.min(99.9, (100 * read) / total) : 0;
              return (
                <TableRow key={`${t.db}.${t.tbl}`} data-testid={`device-table-row-${t.db}-${t.tbl}`}>
                  <TableCell className="font-mono text-xs">{t.db}.<span className="font-semibold">{t.tbl}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{MODE_LABELS[t.mode] || t.mode || "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={pct} className="h-1.5 flex-1" data-testid="export-progress-bar" />
                      <span className="w-12 text-right text-xs num">{pct.toFixed(0)}%</span>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground num" data-testid={`export-detail-${t.db}-${t.tbl}`}>
                      {done ? `${fmtNum(confirmed)} / ${fmtNum(total)}`
                        : `${fmtNum(read)} read · ${fmtNum(confirmed)} confirmed / ${fmtNum(total)}`}
                      {t.export_status && !done ? ` · ${t.export_status}` : ""}
                      {t.eta_s ? ` · ${fmtDuration(t.eta_s)} left` : ""}
                    </div>
                    {t.export_error && <div className="mt-1 text-[11px] text-[hsl(var(--bad))]">{t.export_error}</div>}
                    {t.time_indexed === false && t.live_every_s >= 600 && (
                      <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-400" data-testid={`index-hint-${t.db}-${t.tbl}`}>
                        Live sync only every {Math.round(t.live_every_s / 60)} min: no index on {t.time_column}. Run once in MySQL:
                        <code className="ml-1 rounded bg-secondary px-1 py-0.5 font-mono">ALTER TABLE `{t.db}`.`{t.tbl}` ADD INDEX ix_{t.time_column} (`{t.time_column}`);</code>
                      </div>
                    )}
                    {t.live_every_s > 0 && t.live_every_s < 600 && t.export_status === "done" && (
                      <div className="mt-1 text-[11px] text-muted-foreground">Live sync every {t.live_every_s} s</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-xs num">{fmtNum(t.source_count ?? t.est_rows)}{t.source_count_exact ? "" : " est."}</TableCell>
                  <TableCell className="text-right text-xs num">{fmtBytes(t.size_bytes)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{relTime(t.last_received_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Info({ label, value, testId }) {
  return (
    <div className="flex justify-between gap-4 py-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium num" data-testid={testId}>{value ?? "—"}</span>
    </div>
  );
}

export default function DeviceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: d, mutate, error } = useSWR(`/devices/${id}`, fetcher, { refreshInterval: 10000 });
  const [key, setKey] = useState(null);
  const [confirm, setConfirm] = useState(null);

  if (error) return <div className="text-sm text-muted-foreground">Server not found. <Link to="/connections" className="text-primary">Back</Link></div>;
  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-secondary" />;
  const hb = d.heartbeat || {};
  const total = hb.export_rows_total || 0;
  const read = Math.min(total, hb.export_rows_read ?? hb.export_rows_done ?? 0);
  const confirmed = Math.min(total, hb.export_rows_done || 0);
  const pct = total ? Math.min(100, (100 * read) / total) : 0;

  const act = async (kind) => {
    try {
      if (kind === "rotate") {
        const { data } = await api.post(`/devices/${id}/rotate`);
        setKey(data.device_key);
        toast.success("New key created. Update it on the server.");
      } else if (kind === "revoke") {
        await api.post(`/devices/${id}/revoke`);
        toast.success("Key revoked. The server can no longer upload.");
      } else if (kind === "delete") {
        await api.delete(`/devices/${id}?purge_data=true`);
        toast.success("Server and its data removed");
        nav("/connections");
        return;
      }
      mutate();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <div>
      <Link to="/connections" className="mb-4 inline-flex items-center gap-1 text-sm text-primary" data-testid="back-to-connections-link">
        <ArrowLeft className="h-4 w-4" /> Connections
      </Link>
      <PageHeader title={d.name} subtitle={`${d.site || "No location"} · Device ${d.id} · key ${d.key_prefix}…`}>
        <StatusBadge status={d.status} testId="edge-server-status-badge" className="self-center" />
        <Button variant="secondary" className="rounded-full" onClick={() => setConfirm("rotate")} data-testid="device-key-rotate-button"><RotateCw className="mr-1.5 h-4 w-4" />Rotate key</Button>
        <Button variant="secondary" className="rounded-full" onClick={() => setConfirm("revoke")} disabled={d.key_status === "revoked"} data-testid="device-key-revoke-button"><ShieldOff className="mr-1.5 h-4 w-4" />Revoke</Button>
        <Button variant="ghost" className="rounded-full text-[hsl(var(--bad))]" onClick={() => setConfirm("delete")} data-testid="device-delete-button"><Trash2 className="h-4 w-4" /></Button>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Last heartbeat" value={relTime(d.last_heartbeat_at)} sub={fmtTime(d.last_heartbeat_at)} testId="stat-last-heartbeat" />
        <Stat label="Last data" value={relTime(d.last_data_at)} sub={hb.last_sent_rows != null ? `${fmtNum(hb.last_sent_rows)} rows in last batch` : null} testId="stat-last-data" />
        <Stat label="Rows received" value={fmtNum(d.rows_received_total)} testId="stat-device-rows" />
        <Stat label={d.status === "online" || d.status === "syncing" || d.status === "paused" ? "Phase" : "Last reported phase"} value={hb.phase || "—"} sub={hb.queue_items != null ? `${fmtNum(hb.queue_items)} batches queued (${fmtBytes(hb.queue_bytes)})` : null} testId="stat-phase" />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="surface p-5 lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold md:text-lg">Rows received per minute</h2>
            <span className="text-xs text-muted-foreground">last 3 hours · <span className="text-[hsl(var(--chart-1))]">live</span> / <span className="text-[hsl(var(--chart-2))]">export</span></span>
          </div>
          <ReceivedChart deviceId={id} />
        </div>
        <div className="surface p-5">
          <h2 className="mb-2 text-base font-semibold md:text-lg">Initial export</h2>
          <Progress value={pct} className="h-2" data-testid="overall-export-progress-bar" />
          <div className="mt-2 text-xs text-muted-foreground num" data-testid="device-export-progress">
            <span data-testid="overall-export-text">{fmtNum(read)} of {fmtNum(total)} rows read from MySQL · {pct.toFixed(1)}%</span>
            {hb.read_rows_per_sec ? ` · reading ${fmtNum(Math.round(hb.read_rows_per_sec))} rows/s` : ""}
          </div>
          <div className="mt-1 text-xs text-muted-foreground num" data-testid="device-export-confirmed">
            {fmtNum(confirmed)} confirmed by the dashboard{total ? ` (${((100 * confirmed) / total).toFixed(1)}%)` : ""}
            {hb.rows_per_sec ? ` · ${fmtNum(Math.round(hb.rows_per_sec))} rows/s` : ""}{hb.export_eta_s ? ` · ~${fmtDuration(hb.export_eta_s)} left` : ""}
          </div>
          <div className="mt-4 divide-y divide-border/70">
            <Info label="Host" value={hb.hostname} testId="device-hostname" />
            <Info label="Edge Sender" value={hb.version} />
            <Info label="MySQL" value={hb.mysql_version ? `${hb.mysql_version}${hb.mysql_ok ? "" : " (not connected)"}` : null} />
            <Info label="Time zone" value={hb.mysql_time_zone ? `${hb.mysql_time_zone} (UTC${hb.tz_offset_minutes >= 0 ? "+" : ""}${(hb.tz_offset_minutes || 0) / 60})` : null} />
            <Info label="Uptime" value={fmtDuration(hb.uptime_s)} />
            <Info label="Sender memory" value={hb.memory_mb != null ? `${fmtNum(hb.memory_mb)} MB` : null} testId="device-memory" />
            <Info label="Parallel uploads" value={hb.upload_parallel != null ? `${hb.upload_parallel}` : null} testId="device-upload-parallel" />
            <Info label="Public IP seen" value={d.last_seen_ip} />
          </div>
        </div>
      </div>

      <ExportTable tables={d.tables || []} />

      <div className="surface mt-6 p-5" data-testid="device-errors-panel">
        <h2 className="mb-3 text-base font-semibold md:text-lg">Recent errors reported by the server</h2>
        {(d.recent_errors || []).length === 0 ? <p className="text-sm text-muted-foreground">No errors reported.</p> : (
          <ul className="space-y-2 font-mono text-xs">
            {[...d.recent_errors].reverse().map((e, i) => (
              <li key={i} className="rounded-xl bg-secondary px-3 py-2"><span className="text-muted-foreground">{fmtTime(e.at)}</span> {e.msg}</li>
            ))}
          </ul>
        )}
      </div>

      <Confirm open={confirm === "rotate"} onOpenChange={() => setConfirm(null)} testId="confirm-rotate" title="Rotate device key?"
        text="A new key is created and the old one stops working immediately. Uploads pause until the new key is entered on the server." action="Rotate key" onConfirm={() => act("rotate")} />
      <Confirm open={confirm === "revoke"} onOpenChange={() => setConfirm(null)} testId="confirm-revoke" title="Revoke device key?"
        text="The server will be refused on every upload until you rotate to a new key. Data on the server stays queued." action="Revoke" onConfirm={() => act("revoke")} />
      <Confirm open={confirm === "delete"} onOpenChange={() => setConfirm(null)} testId="confirm-delete" title="Delete this server?"
        text="This removes the server, its key and all data received from it. This cannot be undone." action="Delete" onConfirm={() => act("delete")} />
      <KeyRevealDialog open={!!key} onClose={() => setKey(null)} deviceKey={key} deviceName={d.name} />
    </div>
  );
}
